// Environment detection and configuration utility

export type Environment = 'development' | 'staging' | 'production';

export interface EnvironmentConfig {
    name: Environment;
    displayName: string;
    titleSuffix: string;
    indicatorColor: string;
    alertColor: string;
    showIndicator: boolean;
}

class EnvironmentService {
    private environment: Environment;
    private config: EnvironmentConfig;

    constructor() {
        // Get environment from React app env var, fallback to production for safety
        this.environment = (process.env.REACT_APP_ENVIRONMENT as Environment) || 'production';
        this.config = this.getEnvironmentConfig();
    }

    private getEnvironmentConfig(): EnvironmentConfig {
        switch (this.environment) {
            case 'development':
                return {
                    name: 'development',
                    displayName: 'DEV',
                    titleSuffix: ' - Dev',
                    indicatorColor: '#4caf50', // Green
                    alertColor: '#ffeb3b',     // Yellow
                    showIndicator: true
                };
            case 'staging':
                return {
                    name: 'staging',
                    displayName: 'STAGE/DEMO',
                    titleSuffix: ' - Stage',
                    indicatorColor: '#ff9800', // Orange/Yellow
                    alertColor: '#f44336',     // Red
                    showIndicator: true
                };
            case 'production':
            default:
                return {
                    name: 'production',
                    displayName: 'PROD',
                    titleSuffix: '',
                    indicatorColor: '',
                    alertColor: '#f44336',     // Red
                    showIndicator: false
                };
        }
    }

    public getCurrentEnvironment(): Environment {
        return this.environment;
    }

    public getConfig(): EnvironmentConfig {
        return this.config;
    }

    public isProduction(): boolean {
        return this.environment === 'production';
    }

    public isDevelopment(): boolean {
        return this.environment === 'development';
    }

    public isStaging(): boolean {
        return this.environment === 'staging';
    }

    public getPageTitle(baseTitle: string = 'Point Sails'): string {
        return baseTitle + this.config.titleSuffix;
    }
}

// Export singleton instance
export const environmentService = new EnvironmentService();

// Export convenience functions
export const getCurrentEnvironment = () => environmentService.getCurrentEnvironment();
export const getEnvironmentConfig = () => environmentService.getConfig();
export const isProduction = () => environmentService.isProduction();
export const isDevelopment = () => environmentService.isDevelopment();
export const isStaging = () => environmentService.isStaging();
export const getPageTitle = (baseTitle?: string) => environmentService.getPageTitle(baseTitle);
